import { useEffect, useRef, useState } from 'react';

export default function ProjectGallery() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const projects = [
    {
      title: 'Blog Series "Trilogi Menuju Bumi"',
      description: 'A compelling blog series exploring themes of journey, discovery, and connection to earth and nature.',
      image: 'https://public.readdy.ai/ai/img_res/ee411afe787b98cf61df308d7b3275fe.jpg',
      category: 'Blog Series',
      link: 'https://clorentt.blogspot.com/'
    },
    {
      title: 'Life\'s Series — Clorenarative',
      description: 'A narrative exploration of urban life, capturing the essence of city stories and human experiences.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20urban%20cityscape%20with%20artistic%20storytelling%20elements%2C%20featuring%20silhouettes%20of%20people%20in%20city%20environment%2C%20contemporary%20illustration%20style%2C%20muted%20color%20palette%20with%20pops%20of%20purple%20and%20rose%2C%20clean%20geometric%20shapes%2C%20minimalist%20urban%20aesthetic%2C%20simple%20clean%20background&width=600&height=400&seq=project-urban&orientation=landscape',
      category: 'Narrative Series',
      link: 'https://clorentiash.blogspot.com/?m=1'
    },
    {
      title: 'Mantra Swara by Wix — "Untold For B"',
      description: 'Published poetry book featuring intimate verses and unspoken emotions, crafted with lyrical beauty.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20poetry%20book%20cover%20design%20with%20flowing%20calligraphy%20elements%2C%20featuring%20soft%20watercolor%20textures%2C%20romantic%20purple%20and%20rose%20color%20scheme%2C%20delicate%20floral%20accents%2C%20artistic%20typography%2C%20dreamy%20and%20emotional%20atmosphere%2C%20clean%20simple%20background%20perfect%20for%20poetry&width=600&height=400&seq=project-poetry&orientation=landscape',
      category: 'Published Book',
      link: 'https://clorentia.wixsite.com/mantra-swara---clore'
    },
    {
      title: 'Website with Team P2A',
      description: 'Collaborative web development project showcasing creative media and digital storytelling capabilities.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20modern%20workspace%20with%20creative%20design%20elements%2C%20featuring%20sleek%20laptop%20displaying%20beautiful%20website%20interface%2C%20soft%20natural%20lighting%2C%20aesthetic%20purple%20and%20rose%20gradient%20accents%2C%20minimalist%20desk%20setup%20with%20artistic%20touches%2C%20professional%20creative%20atmosphere%2C%20clean%20sophisticated%20background%20with%20subtle%20textures%2C%20contemporary%20digital%20design%20aesthetic&width=600&height=400&seq=project-website-new&orientation=landscape',
      category: 'Web Development'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-7xl">
        <div className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-rose-500 bg-clip-text text-transparent" style={{ fontFamily: '"Playfair Display", serif' }}>
              Project Gallery
            </h2>
            <p className="text-xl text-gray-600" style={{ fontFamily: '"Lora", serif' }}>
              Here are some of the projects I worked on
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-rose-500 mx-auto rounded-full mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => {
              const CardWrapper = project.link ? 'a' : 'div';
              const cardProps = project.link ? { href: project.link, target: '_blank', rel: 'noopener noreferrer' } : {};
              
              return (
                <CardWrapper
                  key={index}
                  {...cardProps}
                  className={`group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 ${project.link ? 'cursor-pointer' : ''} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${200 + index * 100}ms` }}
                >
                  {project.image && (
                    <div className="relative overflow-hidden h-64">
                      <img 
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-semibold text-purple-600">
                          {project.category}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="p-8">
                    {!project.image && (
                      <div className="mb-4">
                        <span className="px-4 py-2 bg-purple-100 rounded-full text-sm font-semibold text-purple-600">
                          {project.category}
                        </span>
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-purple-600 transition-colors duration-300" style={{ fontFamily: '"Playfair Display", serif' }}>
                      {project.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" style={{ fontFamily: '"Lora", serif' }}>
                      {project.description}
                    </p>
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
